/**
 * @ken/module-employees — frontend entry point.
 *
 * Story-page shape (ADR-0081/0082, PARTY archetype): a list page at
 * /employees and a routed full-page story detail at employees/$id, with the
 * tabs Overview / Notes / Activity. Clicking a person's name navigates to
 * employees/$id/overview.
 *
 * There is no embedded manifest here any more (CV-19). This file used to
 * carry a second copy of modules/employees/manifest.yaml, with a comment
 * warning that the classes module's copy had already drifted from its own
 * YAML. "If you change one, change both" is not a rule anyone keeps: not one
 * of the twenty-one pairs matched. The build only ever read the YAML, and
 * parsing the copy in the browser dragged a YAML parser into every visitor's
 * first download. The YAML is now the only copy.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadEmployeeStoryTabs = () => import("./routes/story/employee-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const EmployeeListPage = lazyScreen(
  () => import("./routes/employee-list"),
  "EmployeeListPage",
);
const EmployeeStoryDetail = lazyScreen(
  () => import("./routes/story/employee-story-detail"),
  "EmployeeStoryDetail",
);
const EmployeeActivityRoute = lazyScreen(
  loadEmployeeStoryTabs,
  "EmployeeActivityRoute",
);
const EmployeeNotesRoute = lazyScreen(
  loadEmployeeStoryTabs,
  "EmployeeNotesRoute",
);
const EmployeeOverviewRoute = lazyScreen(
  loadEmployeeStoryTabs,
  "EmployeeOverviewRoute",
);

// UI-06: employees arrive through the QuickBooks connector, so there is no
// create surface in the cockpit today. `/employees/new` and any other non-id
// segment render the shared RecordNotFound page rather than firing a detail
// read for a record whose id is the word "new" and painting nothing.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "employees",
    // The list's filter/search/highlight are URL-borne so back-navigation from
    // the story page restores them (ADR-0081 §3).
    validateSearch: (raw: Record<string, unknown>): EmployeeListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["employment"] === "string" && raw["employment"]
        ? { employment: raw["employment"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: EmployeeListPage,
  });

  // UI-06: a STATIC route for the literal `new`, registered BEFORE the id
  // route so the id route can never match it.
  const literalNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "employees/new",
    component: RecordNotFound,
  });

  // ── Story-page detail (ADR-0081): parent employees/$id shell + routed tab
  //    children + an index redirect to …/overview. ─────────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "employees/$id",
    // UI-06.1's id-shape guard: a segment that could never be a record id
    // stops here, BEFORE any detail or graph request is issued.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    validateSearch: (raw: Record<string, unknown>): EmployeeStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["employment"] === "string" && raw["employment"]
        ? { employment: raw["employment"] }
        : {}),
    }),
    component: EmployeeStoryDetail,
  });

  // Index redirect employees/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/employees/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: EmployeeOverviewRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: EmployeeNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: EmployeeActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [detailIndexRoute, overviewRoute, notesRoute, activityRoute],
  );

  // Static before parameterised.
  return [listRoute, literalNewRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface EmployeeListSearch {
  q?: string;
  /** Liveness: archived_at. */
  filter_status?: string;
  /** Employment fact: released_date. A different question from the above. */
  employment?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

/** Story-detail search params (carry the list's filter/search). */
export interface EmployeeStorySearch {
  q?: string;
  filter_status?: string;
  employment?: string;
}
